import { useState, useRef, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Upload,
    FileText,
    X,
    Check,
    Loader2,
    AlertCircle,
    Package,
    ChevronRight,
    Sparkles,
    AlertTriangle,
    Lock,
    Mail,
    Download,
} from 'lucide-react';
import { useAuthStore } from '../../hooks/useAuth';
import POImportReviewTable from './POImportReviewTable';
import type { DraftPO } from './POImportReviewTable';

// ─── Props ──────────────────────────────────────────────────────────────────────

interface POImportWizardProps {
    open: boolean;
    onClose: () => void;
    onComplete: () => void;
}

// ─── Result types ───────────────────────────────────────────────────────────────

interface CreateResult {
    created: { poNumber: string; clientName: string; aliasCreated?: boolean; aliasWarning?: string }[];
    skipped: { poNumber: string; reason: string }[];
    errors: { poNumber: string; details: string }[];
}

// ─── Step indicator labels ──────────────────────────────────────────────────────

const STEPS = [
    { num: 1, label: 'Source' },
    { num: 2, label: 'Review' },
    { num: 3, label: 'Results' },
] as const;

// ─── Helpers ────────────────────────────────────────────────────────────────────

function formatFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ─── Component ──────────────────────────────────────────────────────────────────

export function POImportWizard({ open, onClose, onComplete }: POImportWizardProps) {
    const { token, user } = useAuthStore();
    const isOperationsEntitled = user?.effectiveTier === 'operations';

    // ── Wizard state ────────────────────────────────────────────────────────
    const [step, setStep] = useState(1);
    const [slideDirection, setSlideDirection] = useState<'left' | 'right'>('left');

    // ── Step 1 state ────────────────────────────────────────────────────────
    const [file, setFile] = useState<File | null>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [extracting, setExtracting] = useState(false);
    const [extractError, setExtractError] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // ── Step 2 state ────────────────────────────────────────────────────────
    const [drafts, setDrafts] = useState<DraftPO[]>([]);
    const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
    const [sourceLabel, setSourceLabel] = useState('');
    const [duplicateCount, setDuplicateCount] = useState(0);
    const [warningCount, setWarningCount] = useState(0);
    const [importSessionId, setImportSessionId] = useState<string | undefined>(undefined);
    const [creating, setCreating] = useState(false);
    const [createError, setCreateError] = useState<string | null>(null);

    // ── Step 3 state ────────────────────────────────────────────────────────
    const [results, setResults] = useState<CreateResult | null>(null);

    // ── Reset on open/close ─────────────────────────────────────────────────
    useEffect(() => {
        if (open) {
            setStep(1);
            setSlideDirection('left');
            setFile(null);
            setIsDragging(false);
            setExtracting(false);
            setExtractError(null);
            setDrafts([]);
            setSelectedRows(new Set());
            setSourceLabel('');
            setDuplicateCount(0);
            setWarningCount(0);
            setImportSessionId(undefined);
            setCreating(false);
            setCreateError(null);
            setResults(null);
        }
    }, [open]);

    // ── Navigation helpers ──────────────────────────────────────────────────
    const goToStep = (target: number) => {
        setSlideDirection(target > step ? 'left' : 'right');
        setStep(target);
    };

    const validateAndSelectFile = useCallback((candidate: File) => {
        const lowerName = candidate.name.toLowerCase();
        if (lowerName.endsWith('.xlsx')) {
            if (!isOperationsEntitled) {
                setFile(null);
                setExtractError('Excel (.xlsx) bulk PO import requires the Operations plan ($199 USD/month per facility). Starter includes CSV (.csv) PO import. Contact support@baywhiz.com to upgrade.');
                return;
            }
            setFile(candidate);
            setExtractError(null);
            return;
        }
        if (lowerName.endsWith('.csv')) {
            setFile(candidate);
            setExtractError(null);
            return;
        }
        setFile(null);
        setExtractError(
            isOperationsEntitled
                ? 'Unsupported file format. Please upload a CSV (.csv) or Excel (.xlsx) file.'
                : 'Unsupported file format. Starter plan supports CSV (.csv) PO import. Upgrade to Operations ($199/mo) for Excel (.xlsx) bulk PO import.'
        );
    }, [isOperationsEntitled]);

    // ── Drag & Drop handlers ────────────────────────────────────────────────
    const handleDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    }, []);

    const handleDragLeave = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    }, []);

    const handleDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        const dropped = e.dataTransfer.files?.[0];
        if (dropped) {
            validateAndSelectFile(dropped);
        }
    }, [validateAndSelectFile]);

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const selected = e.target.files?.[0];
        if (selected) {
            validateAndSelectFile(selected);
        }
    };

    const removeFile = () => {
        setFile(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleDownloadSampleCsv = () => {
        const sampleCsv = [
            'PO Number,Client Name,Type,Due Date,Priority,Skids,Weight,Description',
            'PO-2026-1001,Acme Foods,inbound,2026-11-15,standard,12,18000,Dry grocery pallets',
            'PO-2026-1002,Pacific Wholesale,inbound,2026-11-16,urgent,8,14500,Beverage cases',
        ].join('\n');
        const blob = new Blob([sampleCsv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'baywhiz-po-import-template.csv';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    // ── Extract POs (Step 1 → 2) ───────────────────────────────────────────
    const canExtract = !!file;

    const handleExtract = async () => {
        if (!file) return;
        setExtracting(true);
        setExtractError(null);

        try {
            const formData = new FormData();
            formData.append('file', file);
            const res = await fetch('/api/warehouse-pos/import/preview', {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` },
                body: formData,
            });
            setSourceLabel(file.name);

            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.message || err.error || `Extraction failed (${res.status})`);
            }

            const data = await res.json();
            const extractedDrafts: DraftPO[] = data.drafts || [];

            setDrafts(extractedDrafts);

            // Select valid rows by default (using 1-based _rowIndex matching POImportReviewTable)
            const validRowIndices = extractedDrafts
                .filter((d: DraftPO) => !!d.poNumber && !!d.clientName && !d._isDuplicate)
                .map((d: DraftPO) => d._rowIndex);
            setSelectedRows(new Set(validRowIndices));
            setDuplicateCount(data.duplicateCount ?? 0);
            setWarningCount(data.warningCount ?? 0);
            if (data.importSessionId) {
                setImportSessionId(data.importSessionId);
            }

            goToStep(2);
        } catch (err: any) {
            setExtractError(err.message || 'Failed to extract purchase orders. Please try again.');
        } finally {
            setExtracting(false);
        }
    };

    // ── Create Selected (Step 2 → 3) ────────────────────────────────────────
    const selectedDrafts = drafts.filter((d: DraftPO) => selectedRows.has(d._rowIndex));

    const handleCreate = async () => {
        if (selectedDrafts.length === 0) return;
        setCreating(true);
        setCreateError(null);

        try {
            // V1.4C: Map internal draft state to explicit aliasOption payload
            const mappedDrafts = selectedDrafts.map((d: DraftPO) => {
                const {
                    _rowIndex, _confidence, _fieldConfidence, _warnings, _isDuplicate, 
                    _existingPoId, _source, _customerSuggestions, _originalClientName, _saveAsAlias,
                    ...rest
                } = d;

                return {
                    ...rest,
                    aliasOption: _saveAsAlias && _originalClientName && rest.customerId ? {
                        saveAlias: true,
                        originalName: _originalClientName,
                        selectedCustomerId: rest.customerId
                    } : undefined
                };
            });

            const res = await fetch('/api/warehouse-pos/import/create', {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ importSessionId, drafts: mappedDrafts, sourceLabel }),
            });

            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.message || err.error || `Creation failed (${res.status})`);
            }

            const data: CreateResult = await res.json();
            setResults(data);
            goToStep(3);
            onComplete();
        } catch (err: any) {
            setCreateError(err.message || 'Failed to create purchase orders.');
            // Show error in results step
            setResults({
                created: [],
                skipped: [],
                errors: [{ poNumber: 'Batch', details: err.message || 'Network error' }],
            });
            goToStep(3);
        } finally {
            setCreating(false);
        }
    };

    // ── Handle Done ─────────────────────────────────────────────────────────
    const handleDone = () => {
        onClose();
    };

    const handleSelectionChange = (selected: Set<number>) => {
        setSelectedRows(selected);
    };

    // ── Animation variants ──────────────────────────────────────────────────
    const slideVariants = {
        enter: (dir: 'left' | 'right') => ({
            x: dir === 'left' ? 60 : -60,
            opacity: 0,
        }),
        center: { x: 0, opacity: 1 },
        exit: (dir: 'left' | 'right') => ({
            x: dir === 'left' ? -60 : 60,
            opacity: 0,
        }),
    };

    // ── Render nothing if not open ──────────────────────────────────────────
    if (!open) return null;

    // ═════════════════════════════════════════════════════════════════════════
    // RENDER
    // ═════════════════════════════════════════════════════════════════════════

    return (
        <AnimatePresence>
            {open && (
                <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
                    {/* ── Backdrop ──────────────────────────────────────── */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                    />

                    {/* ── Modal Window ──────────────────────────────────── */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                        className="relative w-full max-w-5xl bg-white dark:bg-slate-900/95 backdrop-blur-xl border border-slate-200 dark:border-primary/50 rounded-2xl shadow-2xl dark:shadow-[0_0_50px_rgba(8,145,178,0.2)] flex flex-col"
                        style={{ maxHeight: '90vh' }}
                    >
                        {/* ── Header ───────────────────────────────────── */}
                        <div className="shrink-0 p-6 border-b border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-white/5 rounded-t-2xl">
                            <div className="flex items-start justify-between">
                                <div>
                                    <h2 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white font-outfit flex items-center gap-2">
                                        <Package className="text-primary" size={24} />
                                        Import Purchase Orders
                                    </h2>
                                    <p className="text-slate-500 dark:text-slate-400 text-xs sm:text-sm mt-0.5 flex items-center gap-1.5">
                                        <Sparkles size={14} className="text-amber-500" />
                                        {isOperationsEntitled
                                            ? 'CSV (.csv) & Excel (.xlsx) bulk import'
                                            : 'CSV (.csv) import — Starter Plan'}
                                    </p>
                                </div>
                                <button
                                    onClick={onClose}
                                    className="text-slate-400 hover:text-slate-900 dark:text-slate-500 dark:hover:text-white transition-colors p-1"
                                >
                                    <X size={24} />
                                </button>
                            </div>

                            {/* ── Step Indicator ─────────────────────────── */}
                            <div className="flex items-center justify-center mt-6 gap-0">
                                {STEPS.map((s, idx) => (
                                    <div key={s.num} className="flex items-center">
                                        {/* Circle */}
                                        <div className="flex flex-col items-center">
                                            <div
                                                className={`
                                                    w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-300
                                                    ${step > s.num
                                                        ? 'bg-green-500 text-white shadow-md shadow-green-500/30'
                                                        : step === s.num
                                                            ? 'bg-blue-600 text-white shadow-md shadow-blue-600/30'
                                                            : 'bg-slate-200 dark:bg-slate-700 text-slate-400 dark:text-slate-500'
                                                    }
                                                `}
                                            >
                                                {step > s.num ? (
                                                    <Check size={16} strokeWidth={3} />
                                                ) : (
                                                    s.num
                                                )}
                                            </div>
                                            <span
                                                className={`
                                                    text-[10px] font-semibold mt-1.5 uppercase tracking-wider transition-colors duration-300
                                                    ${step >= s.num
                                                        ? 'text-slate-700 dark:text-slate-300'
                                                        : 'text-slate-400 dark:text-slate-600'
                                                    }
                                                `}
                                            >
                                                {s.label}
                                            </span>
                                        </div>
                                        {/* Connector line */}
                                        {idx < STEPS.length - 1 && (
                                            <div className="flex items-center mx-3 mb-5">
                                                <div
                                                    className={`
                                                        w-16 sm:w-24 h-0.5 rounded-full transition-colors duration-500
                                                        ${step > s.num
                                                            ? 'bg-green-400'
                                                            : 'bg-slate-200 dark:bg-slate-700'
                                                        }
                                                    `}
                                                />
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* ── Content Area ─────────────────────────────── */}
                        <div className="flex-1 overflow-y-auto custom-scrollbar">
                            <AnimatePresence mode="wait" custom={slideDirection}>
                                {/* ────── STEP 1: SOURCE INPUT ────── */}
                                {step === 1 && (
                                    <motion.div
                                        key="step-1"
                                        custom={slideDirection}
                                        variants={slideVariants}
                                        initial="enter"
                                        animate="center"
                                        exit="exit"
                                        transition={{ duration: 0.25, ease: 'easeInOut' }}
                                        className="p-6"
                                    >
                                        {!isOperationsEntitled && (
                                            <div
                                                data-testid="po-import-starter-excel-gate"
                                                className="mb-5 rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3"
                                            >
                                                <div className="space-y-1">
                                                    <div className="flex items-center gap-2">
                                                        <Lock size={14} className="text-amber-500 shrink-0" />
                                                        <span className="text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-600 dark:text-amber-400 border border-amber-500/30">
                                                            Starter Plan ($99/mo) — CSV Included
                                                        </span>
                                                    </div>
                                                    <p className="text-xs text-slate-700 dark:text-slate-300 font-medium">
                                                        Basic CSV (<code className="font-mono">.csv</code>) PO import is included in Starter. Upgrade to <strong>Operations ($199 USD/month per facility)</strong> for Excel (<code className="font-mono">.xlsx</code>) bulk PO import and Import Session History.
                                                    </p>
                                                </div>
                                                <a
                                                    href="mailto:support@baywhiz.com?subject=Upgrade%20to%20BayWhiz%20Operations%20Plan"
                                                    className="inline-flex items-center justify-center gap-1.5 px-3.5 py-2 rounded-lg bg-amber-500 hover:bg-amber-600 text-slate-950 font-bold text-xs transition-colors shrink-0"
                                                >
                                                    <Mail size={14} />
                                                    support@baywhiz.com
                                                </a>
                                            </div>
                                        )}

                                        {/* Upload File Content */}
                                        <div>
                                            {!file ? (
                                                <div
                                                    onDragOver={handleDragOver}
                                                    onDragLeave={handleDragLeave}
                                                    onDrop={handleDrop}
                                                    onClick={() => fileInputRef.current?.click()}
                                                    className={`
                                                        border-2 border-dashed rounded-xl p-12 flex flex-col items-center justify-center gap-4 cursor-pointer transition-all duration-200
                                                        ${isDragging
                                                            ? 'border-blue-500 bg-blue-50 dark:bg-blue-500/10'
                                                            : 'border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-black/20 hover:border-blue-400 dark:hover:border-blue-500 hover:bg-blue-50/50 dark:hover:bg-blue-500/5'
                                                        }
                                                    `}
                                                >
                                                    <div className={`
                                                        w-14 h-14 rounded-2xl flex items-center justify-center transition-colors
                                                        ${isDragging
                                                            ? 'bg-blue-100 dark:bg-blue-500/20'
                                                            : 'bg-slate-200 dark:bg-slate-700'
                                                        }
                                                    `}>
                                                        <FileText
                                                            size={28}
                                                            className={isDragging
                                                                ? 'text-blue-600 dark:text-blue-400'
                                                                : 'text-slate-400 dark:text-slate-500'
                                                            }
                                                        />
                                                    </div>
                                                    <div className="text-center">
                                                        <p className="text-slate-700 dark:text-slate-300 font-semibold text-sm">
                                                            {isDragging
                                                                ? 'Drop file here'
                                                                : isOperationsEntitled
                                                                    ? 'Drop CSV (.csv) or Excel (.xlsx) file'
                                                                    : 'Drop CSV (.csv) file'}
                                                        </p>
                                                        <p className="text-slate-400 dark:text-slate-500 text-xs mt-1">
                                                            or click to browse
                                                        </p>
                                                    </div>
                                                    <input
                                                        ref={fileInputRef}
                                                        type="file"
                                                        accept={isOperationsEntitled ? '.csv,.xlsx' : '.csv'}
                                                        onChange={handleFileSelect}
                                                        className="hidden"
                                                    />
                                                </div>
                                            ) : (
                                                /* File selected — show summary */
                                                <div className="border border-slate-200 dark:border-white/10 rounded-xl p-4 bg-slate-50 dark:bg-black/20 flex items-center gap-4">
                                                    <div className="w-12 h-12 rounded-xl bg-blue-100 dark:bg-blue-500/20 flex items-center justify-center shrink-0">
                                                        <FileText size={24} className="text-blue-600 dark:text-blue-400" />
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-slate-900 dark:text-white font-semibold text-sm truncate">
                                                            {file.name}
                                                        </p>
                                                        <p className="text-slate-500 dark:text-slate-400 text-xs">
                                                            {formatFileSize(file.size)}
                                                        </p>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={removeFile}
                                                        className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 transition-all"
                                                    >
                                                        <X size={18} />
                                                    </button>
                                                </div>
                                            )}

                                            {/* Sample CSV Template Download */}
                                            <div className="mt-4 flex flex-col sm:flex-row sm:items-center justify-between gap-2 rounded-xl border border-slate-200 dark:border-white/10 bg-slate-50/70 dark:bg-white/5 px-4 py-3">
                                                <div className="text-xs text-slate-600 dark:text-slate-400">
                                                    <span className="font-semibold text-slate-800 dark:text-slate-200">Required columns:</span>{' '}
                                                    <code className="font-mono">PO Number</code>, <code className="font-mono">Client Name</code>{' '}
                                                    <span className="text-slate-400 dark:text-slate-500">
                                                        (Optional: Type, Due Date, Priority, Skids, Weight, Description)
                                                    </span>
                                                </div>
                                                <button
                                                    type="button"
                                                    data-testid="download-sample-csv-btn"
                                                    onClick={handleDownloadSampleCsv}
                                                    className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-500/10 hover:bg-blue-100 dark:hover:bg-blue-500/20 border border-blue-200 dark:border-blue-500/30 transition-colors shrink-0"
                                                >
                                                    <Download size={14} />
                                                    Download Sample CSV Template
                                                </button>
                                            </div>
                                        </div>

                                        {/* Error Banner */}
                                        <AnimatePresence>
                                            {extractError && (
                                                <motion.div
                                                    initial={{ opacity: 0, y: -8 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    exit={{ opacity: 0, y: -8 }}
                                                    className="mt-4 flex items-start gap-3 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 rounded-xl p-4"
                                                >
                                                    <AlertCircle size={18} className="text-red-500 shrink-0 mt-0.5" />
                                                    <div>
                                                        <p className="text-red-700 dark:text-red-400 text-sm font-semibold">Extraction failed</p>
                                                        <p className="text-red-600 dark:text-red-400/80 text-xs mt-0.5">{extractError}</p>
                                                    </div>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </motion.div>
                                )}

                                {/* ────── STEP 2: REVIEW DRAFTS ────── */}
                                {step === 2 && (
                                    <motion.div
                                        key="step-2"
                                        custom={slideDirection}
                                        variants={slideVariants}
                                        initial="enter"
                                        animate="center"
                                        exit="exit"
                                        transition={{ duration: 0.25, ease: 'easeInOut' }}
                                        className="p-6"
                                    >
                                        {/* Extraction Summary */}
                                        <div className="flex flex-wrap items-center gap-2 mb-5">
                                            <div className="flex items-center gap-1.5 text-sm text-slate-700 dark:text-slate-300 font-medium">
                                                <Package size={16} className="text-primary" />
                                                Extracted <span className="font-bold text-slate-900 dark:text-white">{drafts.length}</span> POs from{' '}
                                                <span className="font-mono text-xs bg-slate-100 dark:bg-white/10 px-2 py-0.5 rounded-md">
                                                    {sourceLabel}
                                                </span>
                                            </div>
                                            {duplicateCount > 0 && (
                                                <span className="text-xs bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-400 px-2 py-0.5 rounded-full font-semibold">
                                                    {duplicateCount} duplicate{duplicateCount > 1 ? 's' : ''}
                                                </span>
                                            )}
                                            {warningCount > 0 && (
                                                <span className="text-xs bg-orange-100 dark:bg-orange-500/15 text-orange-700 dark:text-orange-400 px-2 py-0.5 rounded-full font-semibold">
                                                    {warningCount} warning{warningCount > 1 ? 's' : ''}
                                                </span>
                                            )}
                                        </div>

                                        {/* Review Table */}
                                        <POImportReviewTable
                                            drafts={drafts}
                                            onDraftsChange={setDrafts}
                                            selectedRows={selectedRows}
                                            onSelectionChange={handleSelectionChange}
                                        />

                                        {/* Create Error Banner */}
                                        <AnimatePresence>
                                            {createError && (
                                                <motion.div
                                                    initial={{ opacity: 0, y: -8 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    exit={{ opacity: 0, y: -8 }}
                                                    className="mt-4 flex items-start gap-3 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 rounded-xl p-4"
                                                >
                                                    <AlertCircle size={18} className="text-red-500 shrink-0 mt-0.5" />
                                                    <p className="text-red-600 dark:text-red-400 text-sm">{createError}</p>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </motion.div>
                                )}

                                {/* ────── STEP 3: RESULTS ────── */}
                                {step === 3 && results && (
                                    <motion.div
                                        key="step-3"
                                        custom={slideDirection}
                                        variants={slideVariants}
                                        initial="enter"
                                        animate="center"
                                        exit="exit"
                                        transition={{ duration: 0.25, ease: 'easeInOut' }}
                                        className="p-6"
                                    >
                                        {/* Summary Cards */}
                                        <div className="grid grid-cols-3 gap-4 mb-8">
                                            {/* Created */}
                                            <div className="bg-green-50 dark:bg-green-500/10 border border-green-200 dark:border-green-500/30 rounded-xl p-5 text-center">
                                                <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-500/20 flex items-center justify-center mx-auto mb-2">
                                                    <Check size={20} className="text-green-600 dark:text-green-400" />
                                                </div>
                                                <p className="text-2xl font-bold text-green-700 dark:text-green-400">
                                                    {results.created.length}
                                                </p>
                                                <p className="text-xs font-semibold text-green-600 dark:text-green-500 uppercase tracking-wider mt-1">
                                                    Created
                                                </p>
                                            </div>
                                            {/* Skipped */}
                                            <div className="bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30 rounded-xl p-5 text-center">
                                                <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-500/20 flex items-center justify-center mx-auto mb-2">
                                                    <ChevronRight size={20} className="text-amber-600 dark:text-amber-400" />
                                                </div>
                                                <p className="text-2xl font-bold text-amber-700 dark:text-amber-400">
                                                    {results.skipped.length}
                                                </p>
                                                <p className="text-xs font-semibold text-amber-600 dark:text-amber-500 uppercase tracking-wider mt-1">
                                                    Skipped
                                                </p>
                                            </div>
                                            {/* Errors */}
                                            <div className="bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 rounded-xl p-5 text-center">
                                                <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-500/20 flex items-center justify-center mx-auto mb-2">
                                                    <AlertCircle size={20} className="text-red-600 dark:text-red-400" />
                                                </div>
                                                <p className="text-2xl font-bold text-red-700 dark:text-red-400">
                                                    {results.errors.length}
                                                </p>
                                                <p className="text-xs font-semibold text-red-600 dark:text-red-500 uppercase tracking-wider mt-1">
                                                    Errors
                                                </p>
                                            </div>
                                        </div>

                                        {/* Created list */}
                                        {results.created.length > 0 && (
                                            <div className="mb-6 space-y-4">
                                                {results.created.filter(po => !po.aliasWarning).length > 0 && (
                                                    <div>
                                                        <h4 className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                                                            <Check size={14} className="text-green-500" />
                                                            Created Successfully
                                                        </h4>
                                                        <div className="space-y-1.5">
                                                            {results.created.filter(po => !po.aliasWarning).map((po, i) => (
                                                                <div
                                                                    key={i}
                                                                    className="flex flex-wrap items-center gap-3 bg-green-50/60 dark:bg-green-500/5 border border-green-100 dark:border-green-500/10 rounded-lg px-4 py-2.5"
                                                                >
                                                                    <span className="font-mono text-sm font-bold text-green-700 dark:text-green-400">
                                                                        {po.poNumber}
                                                                    </span>
                                                                    <span className="text-slate-500 dark:text-slate-400 text-sm">
                                                                        {po.clientName}
                                                                    </span>
                                                                    {po.aliasCreated && (
                                                                        <span className="ml-auto text-[10px] font-bold tracking-wide uppercase text-blue-600 bg-blue-100/50 px-2 py-0.5 rounded border border-blue-200">
                                                                            Alias Saved
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}

                                                {results.created.filter(po => po.aliasWarning).length > 0 && (
                                                    <div>
                                                        <h4 className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                                                            <AlertTriangle size={14} className="text-amber-500" />
                                                            Created with Warnings
                                                        </h4>
                                                        <div className="space-y-1.5">
                                                            {results.created.filter(po => po.aliasWarning).map((po, i) => (
                                                                <div
                                                                    key={i}
                                                                    className="flex flex-wrap items-center gap-3 bg-amber-50/60 dark:bg-amber-500/5 border border-amber-100 dark:border-amber-500/10 rounded-lg px-4 py-2.5"
                                                                >
                                                                    <span className="font-mono text-sm font-bold text-amber-700 dark:text-amber-400">
                                                                        {po.poNumber}
                                                                    </span>
                                                                    <span className="text-slate-500 dark:text-slate-400 text-sm">
                                                                        {po.clientName}
                                                                    </span>
                                                                    <span 
                                                                        className="ml-auto flex items-center gap-1 text-[10px] font-bold tracking-wide uppercase text-amber-600 bg-amber-100/50 px-2 py-0.5 rounded border border-amber-200 cursor-help"
                                                                        title={po.aliasWarning}
                                                                    >
                                                                        <AlertTriangle size={10} />
                                                                        {po.aliasWarning}
                                                                    </span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {/* Skipped list */}
                                        {results.skipped.length > 0 && (
                                            <div className="mb-6">
                                                <h4 className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                                                    <ChevronRight size={14} className="text-amber-500" />
                                                    Skipped
                                                </h4>
                                                <div className="space-y-1.5">
                                                    {results.skipped.map((po, i) => (
                                                        <div
                                                            key={i}
                                                            className="flex items-center gap-3 bg-amber-50/60 dark:bg-amber-500/5 border border-amber-100 dark:border-amber-500/10 rounded-lg px-4 py-2.5"
                                                        >
                                                            <span className="font-mono text-sm font-semibold text-amber-700 dark:text-amber-400">
                                                                {po.poNumber}
                                                            </span>
                                                            <span className="text-slate-500 dark:text-slate-400 text-xs">
                                                                — {po.reason}
                                                            </span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* Errors list */}
                                        {results.errors.length > 0 && (
                                            <div>
                                                <h4 className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-1.5">
                                                    <AlertCircle size={14} className="text-red-500" />
                                                    Errors
                                                </h4>
                                                <div className="space-y-1.5">
                                                    {results.errors.map((po, i) => (
                                                        <div
                                                            key={i}
                                                            className="flex items-center gap-3 bg-red-50/60 dark:bg-red-500/5 border border-red-100 dark:border-red-500/10 rounded-lg px-4 py-2.5"
                                                        >
                                                            <span className="font-mono text-sm font-semibold text-red-700 dark:text-red-400">
                                                                {po.poNumber}
                                                            </span>
                                                            <span className="text-red-600 dark:text-red-400/80 text-xs">
                                                                — {po.details || (po as any).error}
                                                            </span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>

                        {/* ── Footer ───────────────────────────────────── */}
                        <div className="shrink-0 p-4 sm:p-6 border-t border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-white/5 rounded-b-2xl flex items-center justify-between gap-3">
                            {/* Left side */}
                            <div>
                                {step === 2 && (
                                    <button
                                        type="button"
                                        onClick={() => goToStep(1)}
                                        className="px-5 py-2.5 bg-slate-100 hover:bg-slate-200 dark:bg-white/5 dark:hover:bg-white/10 text-slate-700 dark:text-slate-300 rounded-xl font-semibold text-sm transition-all border border-slate-300 dark:border-transparent dark:hover:border-white/10"
                                    >
                                        Back
                                    </button>
                                )}
                            </div>

                            {/* Right side */}
                            <div className="flex items-center gap-3">
                                {step === 1 && (
                                    <button
                                        type="button"
                                        onClick={handleExtract}
                                        disabled={!canExtract || extracting}
                                        className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-xl font-bold text-sm transition-all shadow-md shadow-blue-600/20 hover:shadow-lg hover:shadow-blue-600/30 flex items-center gap-2"
                                    >
                                        {extracting ? (
                                            <>
                                                <Loader2 size={18} className="animate-spin" />
                                                <span>Parsing purchase orders…</span>
                                            </>
                                        ) : (
                                            <>
                                                <Upload size={18} />
                                                Extract POs
                                            </>
                                        )}
                                    </button>
                                )}

                                {step === 2 && (
                                    <button
                                        type="button"
                                        onClick={handleCreate}
                                        disabled={selectedRows.size === 0 || creating}
                                        className="px-6 py-2.5 bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-xl font-bold text-sm transition-all shadow-md shadow-green-600/20 hover:shadow-lg hover:shadow-green-600/30 flex items-center gap-2"
                                    >
                                        {creating ? (
                                            <>
                                                <Loader2 size={18} className="animate-spin" />
                                                Creating…
                                            </>
                                        ) : (
                                            <>
                                                <Check size={18} />
                                                Create Selected ({selectedRows.size})
                                            </>
                                        )}
                                    </button>
                                )}

                                {step === 3 && (
                                    <button
                                        type="button"
                                        onClick={handleDone}
                                        className="px-8 py-2.5 bg-primary hover:bg-primary-600 text-white rounded-xl font-bold text-sm transition-all shadow-md shadow-primary/20 hover:shadow-lg hover:shadow-primary/30 flex items-center gap-2"
                                    >
                                        <Check size={18} />
                                        Done
                                    </button>
                                )}
                            </div>
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
}

export default POImportWizard;
